import * as yup from 'yup';
import { MEETING_TICKET_KEY, SERVICE_SUSPEND_KEY } from '@/api/tickets-constants';

/**
 * Yup schema for the Ticket create + edit form.
 * Ported from legacy Create/schema.ts — field names and conditional branches
 * are verbatim. No i18n dependency (EN-first copy inline).
 *
 * Date comparisons use plain Date arithmetic to avoid needing dayjs plugins
 * (isSameOrAfter / isSameOrBefore require the customParseFormat plugin which
 * is not globally registered in this project).
 */
export const ticketFormSchema = yup.object({
  // Classification
  ticket_type_id: yup
    .number()
    .required('Ticket type is required')
    .moreThan(0, 'Ticket type is required'),
  ticket_type_key: yup.string().default(''),
  ticket_type_group: yup.string().default(''),

  // Meeting-only fields
  ticket_meeting_purpose: yup.string().when('ticket_type_key', {
    is: MEETING_TICKET_KEY,
    then: (s) => s.required('Meeting purpose is required'),
    otherwise: (s) => s.notRequired().default(''),
  }),
  ticket_meeting_via: yup.string().when('ticket_type_key', {
    is: MEETING_TICKET_KEY,
    then: (s) => s.required('Meeting link / location is required'),
    otherwise: (s) => s.notRequired().default(''),
  }),
  ticket_meeting_type: yup.string().default('online'),
  ticket_meeting_channel: yup.string().default(''),

  // Customer / service
  customer_id: yup.number().when('ticket_type_key', {
    is: 'internal_problem',
    then: (s) => s.notRequired().default(0),
    otherwise: (s) =>
      s.required('Customer is required').moreThan(0, 'Customer is required'),
  }),
  service_id: yup.mixed<number | string>().default(0),
  ticket_product_name: yup
    .string()
    .when(['ticket_type_key', 'service_id'], {
      is: (typeKey: string, serviceId: number | string) => {
        if (typeKey === 'internal_problem') return false;
        const hasService =
          serviceId &&
          ((typeof serviceId === 'number' && serviceId > 0) ||
            (typeof serviceId === 'string' && serviceId.trim() !== ''));
        return !hasService;
      },
      then: (s) => s.required('Service or product name is required'),
      otherwise: (s) => s.notRequired().default(''),
    }),

  // Dates — stored as ISO strings or null; comparisons use Date.getTime()
  ticket_requested_at: yup.string().nullable().default(null),
  ticket_time_repply: yup
    .string()
    .nullable()
    .test('not-before-requested', 'Response date cannot be before request date', function (val) {
      if (!val) return true;
      const { ticket_requested_at } = this.parent as { ticket_requested_at?: string | null };
      if (!ticket_requested_at) return true;
      return new Date(val).getTime() >= new Date(ticket_requested_at).getTime();
    })
    .test(
      'not-after-completion',
      'Response date cannot be after estimated completion',
      function (val) {
        if (!val) return true;
        const { ticket_estimated_completion_date } = this.parent as {
          ticket_estimated_completion_date?: string | null;
        };
        if (!ticket_estimated_completion_date) return true;
        return new Date(val).getTime() <= new Date(ticket_estimated_completion_date).getTime();
      },
    )
    .default(null),
  ticket_estimated_completion_date: yup
    .string()
    .nullable()
    .test('not-before-reply', 'Completion date cannot be before response date', function (val) {
      if (!val) return true;
      const { ticket_time_repply } = this.parent as { ticket_time_repply?: string | null };
      if (!ticket_time_repply) return true;
      return new Date(val).getTime() >= new Date(ticket_time_repply).getTime();
    })
    .default(null),
  service_suspend_until: yup.string().nullable().when('ticket_type_key', {
    is: SERVICE_SUSPEND_KEY,
    then: (s) => s.required('Service suspend until date is required'),
    otherwise: (s) => s.nullable().default(null),
  }),
  service_startdate: yup.string().nullable().default(null),
  service_duedate: yup.string().nullable().default(null),

  // Content
  ticket_request_id: yup.number().default(0),
  ticket_content: yup.string().default(''),
  ticket_priority: yup.string().default('low'),
  ticket_status: yup.string().default('opening'),
  ticket_security_tag: yup.string().default('none'),
  ticket_marjorable: yup.boolean().default(false),
  ticket_affected: yup.string().default(''),
  ticket_avoid: yup.string().default(''),
  ticket_root_cause: yup.string().default(''),
  ticket_remedial_measures: yup.string().default(''),
  ticket_internal_root_cause: yup.string().default(''),
  ticket_internal_remedial_measures: yup.string().default(''),
  ticket_internal_avoid: yup.string().default(''),

  // Assignees
  ticket_technical_assignees: yup.array(yup.number().required()).default([]),
  ticket_cs_assignees: yup.array(yup.number().required()).default([]),
  ticket_sale_assignees: yup.array(yup.number().required()).default([]),

  // Edit-only
  ticket_id: yup.number().default(0),
  employee_id: yup.number().default(0),
  customer_journey_id: yup.number().optional(),
});

export type TicketFormValues = yup.InferType<typeof ticketFormSchema>;
